import type { Cliente, Material, User } from '@/db';

function normalize(value?: string) {
  return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
}

export function accessLevel(user: User | null): 'admin' | 'manager' | 'seller' {
  const role = normalize(user?.role);
  if (role.includes('admin') || role.includes('diretor')) return 'admin';
  if (role.includes('gerente') || role.includes('manager')) return 'manager';
  return 'seller';
}

export function canSeeSensitivePricing(user: User | null) {
  return accessLevel(user) !== 'seller';
}

export function canManageMasterData(user: User | null) {
  return accessLevel(user) === 'admin';
}

export function responsibleNames(user: User | null): string[] {
  return user ? [user.name, user.responsibleName || ''].filter(Boolean) : [];
}

export function visibleResponsibleNames(user: User | null): string[] | null {
  if (!user) return [];
  if (accessLevel(user) === 'admin') return null;
  return [...responsibleNames(user), ...(accessLevel(user) === 'manager' ? user.teamResponsibleNames || [] : [])];
}

export function matchesResponsible(user: User | null, responsible?: string) {
  const person = normalize(responsible);
  return !!person && responsibleNames(user).some(name => normalize(name) === person);
}

// Usuários que enxergam TODOS os clientes de uma divisão, independente do
// responsável do cliente (hoje e no futuro: a regra é pela divisão, não por lista).
const DIVISION_WIDE_CLIENT_ACCESS: Record<string, string[]> = {
  'j.pereira@zschimmer-schwarz.com.br': ['Couro'], // José Daniel Pereira
};

function hasDivisionWideAccess(user: User | null, client: Cliente) {
  const divisions = DIVISION_WIDE_CLIENT_ACCESS[normalize(user?.email)] || [];
  return !!client.divisao && divisions.some(division => normalize(division) === normalize(client.divisao));
}

// Regra do cliente: se tem responsável preenchido, só ele (e o gerente da equipe) vê;
// se o responsável está em branco, vale a divisão do cliente (coluna DIVISAO do CIGAM).
export function canAccessClient(user: User | null, client: Cliente) {
  const level = accessLevel(user);
  if (level === 'admin') return true;
  if (hasDivisionWideAccess(user, client)) return true;
  if (!normalize(client.responsavel)) {
    const division = normalize(user?.division);
    return !!division && normalize(client.divisao) === division;
  }
  if (level === 'manager') return (visibleResponsibleNames(user) || []).some(name => normalize(name) === normalize(client.responsavel));
  return matchesResponsible(user, client.responsavel);
}

// Produto que ainda não tem nenhuma linha na Tabela de Preços (logo, nenhum responsável)
// fica visível para quem é da mesma divisão do cadastro (CONTA_GERENCIAL do CIGAM).
function isUnassignedInUserDivision(user: User | null, material: Material) {
  const division = normalize(user?.division);
  return !(material.responsaveis || []).length && !!division && normalize(material.contaGerencial) === division;
}

export function canAccessMaterial(user: User | null, material: Material) {
  const level = accessLevel(user);
  if (level === 'admin') return true;
  if (level === 'manager') return (user?.teamProductCodes || []).includes(material.codigo) || (material.responsaveis || []).some(name => (visibleResponsibleNames(user) || []).some(allowed => normalize(allowed) === normalize(name))) || isUnassignedInUserDivision(user, material);
  if (user?.productCodes) return user.productCodes.includes(material.codigo);
  return (material.responsaveis || []).some((responsible) => matchesResponsible(user, responsible)) || isUnassignedInUserDivision(user, material);
}

export function canAccessQuote(user: User | null, seller: string, division?: string) {
  const names = visibleResponsibleNames(user);
  return names === null || (!!normalize(seller) && names.some(name => normalize(name) === normalize(seller)));
}
